import { fetchDeviceSetting, printerLog } from '@/apis/aipass'
import { deviceSettingsType } from '@/models/device/device-setting'
import { useFileUpload } from './use-file-upload'
import serialNumberToHostName from '@/libs/epos-serial-number-to-host'
import { reportError } from '@/components/apps/error-boundary'

const DEVICE_IN_USE = 'DEVICE_IN_USE'
const DEVICE_OPEN_ERROR = 'DEVICE_OPEN_ERROR'
const CREATE_DEVICE_RETRY_INTERVAL_MS = 500
const CREATE_DEVICE_RETRY_LIMIT = 10

const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

export type EposPrinterConfig = {
  hotelId: string
  connectIdKey: string
  retry?: number
  /** 自動再接続では復帰できないため、切断された接続を呼び出し側で破棄するための通知 */
  onDisconnect?: () => void
}
export interface EposPrinter {
  initialize: (config: EposPrinterConfig) => Promise<{ connection: any; printer: any }>
  printText: (test: string, printer, connection, isStayConnected: boolean) => Promise<void>
  printImage: (
    canvas: HTMLCanvasElement,
    checkinId: string,
    hotelId: string,
    printer,
    connection,
    isStayConnected: boolean,
    reservationUuid?: string,
  ) => Promise<void>
  disconnect: (connection: any, printer: any) => void
}

export const useEposPrinter = () => {
  const { upload: fileUpload } = useFileUpload()

  const connect = async (ip: string, port: number, serialNumber?: string, onDisconnect?: () => void) => {
    const host = serialNumber ? await serialNumberToHostName(serialNumber) : ip
    return await new Promise<any>((resolve, reject) => {
      const ePosDev = new (window as any).epson.ePOSDevice()
      ePosDev.connect(
        host,
        port,
        connectStatus => {
          if (connectStatus !== 'OK' && connectStatus !== 'SSL_CONNECT_OK') {
            reject(`プリンターに接続できません. status:${connectStatus}`)
            return
          }
          resolve(ePosDev)
        },
        { eposprint: true },
      )
      ePosDev.onreconnecting = () => printerLog({ message: 'onreconnecting' })
      ePosDev.onreconnect = () => printerLog({ message: 'onreconnect' })
      ePosDev.ondisconnect = () => {
        printerLog({ message: 'ondisconnect' })
        onDisconnect?.()
      }
    })
  }

  const disconnect = (connection, printer): void => {
    if (!connection?.isConnected()) {
      return
    }
    if (!printer) {
      connection.disconnect()
      return
    }
    connection.deleteDevice(printer, () => connection.disconnect())
  }

  const createDevice = async (connection): Promise<{ deviceObject: any; errorCode: string }> => {
    return new Promise(resolve => {
      connection.createDevice(
        'local_printer',
        connection.DEVICE_TYPE_PRINTER,
        { crypto: false, buffer: false },
        (deviceObject, errorCode) =>
          // デバイスオブジェクトを取得できない場合は null が渡される
          resolve({ deviceObject, errorCode }),
      )
    })
  }

  /**
   * 切断直後はプリンター側でデバイスが解放されるまで DEVICE_IN_USE が返るため、解放されるまで createDevice を繰り返す（SDK 仕様）。
   * createDevice は親機と周辺機器の接続状態に関わらず成功するため、成功しても印刷できることまでは保証しない。
   */
  const createPrinter = async (connection): Promise<{ printer?: any; errorCode: string }> => {
    for (let retry = 0; retry < CREATE_DEVICE_RETRY_LIMIT; retry++) {
      const { deviceObject, errorCode } = await createDevice(connection)
      if (deviceObject && errorCode === 'OK') {
        return { printer: deviceObject, errorCode }
      }
      if (errorCode !== DEVICE_IN_USE) {
        return { errorCode }
      }
      await wait(CREATE_DEVICE_RETRY_INTERVAL_MS)
    }
    return { errorCode: DEVICE_IN_USE }
  }

  /**
   * DEVICE_OPEN_ERROR はデバイスとの接続が切れている場合があり、接続からやり直すと復帰できる（SDK 仕様）ため、
   * このエラーに限り1度だけ接続し直す。
   */
  const openPrinter = async (
    printerSetting: deviceSettingsType,
    config: EposPrinterConfig,
    isRetry = false,
  ): Promise<{ connection: any; printer: any }> => {
    const connection = await connect(printerSetting.ipAddress, printerSetting.port, printerSetting.serialNumber, config.onDisconnect)
    const { printer, errorCode } = await createPrinter(connection)
    if (printer) {
      return { connection, printer }
    }
    // 失敗した接続を残すとプリンター側のセッションが解放されず、次の接続が DEVICE_IN_USE になるため切断する
    connection.disconnect()
    if (errorCode === DEVICE_OPEN_ERROR && !isRetry) {
      return openPrinter(printerSetting, config, true)
    }
    // 他の接続エラーと同じく文字列で reject する（Error にすると errorHandler の alert に "Error:" が付く）
    return Promise.reject(`プリンターに接続できません. code:${errorCode}`)
  }

  const getPrinterToConnectTo = async (hotelId: string, connectIdKey: string): Promise<deviceSettingsType | undefined> => {
    const allDevice = await fetchDeviceSetting(hotelId)
    if (!allDevice) {
      return
    }
    return allDevice.find(device => device.id === localStorage.getItem(connectIdKey))
  }

  const initialize = async (config: EposPrinterConfig): Promise<{ connection: any; printer: any }> => {
    const printerSetting = await getPrinterToConnectTo(config.hotelId, config.connectIdKey)
    if (!printerSetting) {
      throw new Error('Invalid device setting.')
    }

    let printerError
    for (let retry = 0; retry < (config.retry || 1); retry++) {
      try {
        return await openPrinter(printerSetting, config)
      } catch (error) {
        printerError = error
      }
    }
    throw printerError
  }

  const printText = async (test: string, printer, connection, isStayConnected = false) => {
    return new Promise<void>((resolve, reject) => {
      printer.timeout = 10000
      printer.onerror = err => {
        console.log({ err })
        reject(`プリンターが応答しません. status:${err.status} ${err.responseText ?? ''}`)
      }
      printer.onreceive = res => {
        console.log({ res })
        if (!res.success) {
          reject(`プリンターが応答しません. code:${res.code}`)
          return
        }
        if (!isStayConnected) {
          disconnect(connection, printer)
        }
        resolve()
      }

      printer.addText(`${test}\n`)
      printer.addCut(printer.CUT_FEED)
      printer.send()
    })
  }

  const printImage = async (
    canvas: HTMLCanvasElement,
    filename: string,
    hotelId: string,
    printer,
    connection,
    isStayConnected = false,
    reservationUuid?: string,
  ): Promise<void> => {
    await new Promise<void>((resolve, reject) => {
      printer.timeout = 10000
      printer.onerror = err => {
        reject(`プリンターが応答しません. status:${err.status} ${err.responseText ?? ''}`)
      }
      printer.onreceive = res => {
        console.log({ res })
        if (!res.success) {
          reject(`プリンターが応答しません. code:${res.code}`)
          return
        }
        if (!isStayConnected) {
          disconnect(connection, printer)
        }
        resolve()
      }
      printer.addImage(canvas.getContext('2d'), 0, 0, canvas.width, canvas.height)
      printer.addCut(printer.CUT_FEED)
      printer.send()
    })
    try {
      await fileUpload({
        hotelId,
        feature: 'self-ci-printer',
        file: {
          name: `${filename}_w${canvas.width}_h${canvas.height}_s${window.devicePixelRatio}.jpeg`,
          type: 'image/jpeg',
        },
        fileData: canvas.toDataURL('image/jpeg'),
        selfCheckin: { reservationUuid },
      })
    } catch (error) {
      reportError(error)
    }
  }

  return { printText, printImage, initialize, disconnect }
}
